import Axios, { VirtualDeviceApi } from '@/api';
import { DeviceType } from '@/common/constants';
import { supportsLampCmdCs } from '@/common/utils/controllerDisplayName';

const PHYSICAL_MAPPING_TYPE = 1;

const normalizeItems = (items) => {
  if (items == null) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'object') return Object.values(items);
  return [];
};

const lampPortsOf = (it) => {
  const ports = it?.item?.ports;
  if (Array.isArray(ports)) return ports.filter((p) => p != null);
  if (ports != null) return [ports];
  return [];
};

const publishControlSilent = (mqttControl, controllerId, payload) => {
  const hook = mqttControl?.getMqttHook?.();
  if (!hook?.publish || !controllerId) return;
  const prefix = process.env.VUE_APP_MQTT_DEFAULT_TOPIC_PREFIX;
  const mcs = process.env.VUE_APP_MQTT_MCS_TOPIC_PREFIX;
  const suffix = process.env.VUE_APP_MQTT_PUBLISH_TOPIC_SIRFIX;
  hook.publish(`${prefix}/${mcs}/${controllerId}/${suffix}`, payload);
};

export const fetchPhysicalMappingItems = async (buttonDeviceId) => {
  if (buttonDeviceId == null) return [];
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', buttonDeviceId),
      {},
      '',
      { hideLoading: true },
    );
    if (result?.resultCode !== '0000') return [];
    if (Number(result.resultData?.mappingType) !== PHYSICAL_MAPPING_TYPE) return [];
    return normalizeItems(result.resultData?.items);
  } catch (e) {
    console.error('physical button mapping 조회 실패:', buttonDeviceId, e);
    return [];
  }
};

function publishCrossControllerFromItems({
  mqttControl,
  buttonDeviceId,
  sourceControllerId,
  items,
  onoff,
  bright,
  color,
}) {
  const crossMembers = (items || []).filter(
    (it) => Number(it?.type) === DeviceType.Lamp
      && it.controllerId
      && String(it.controllerId) !== String(sourceControllerId),
  );
  if (crossMembers.length === 0) return;

  const resolvedOnoff = onoff === true || onoff === 1 ? 1 : 0;
  const resolvedBright = bright != null ? parseInt(bright, 10) : 0;
  const resolvedColor = color != null ? parseInt(color, 10) : 0;
  const origin = {
    requester: 'physicalMappingHost',
    hostDeviceId: buttonDeviceId,
    hostSerial: sourceControllerId,
    onoff: resolvedOnoff,
    bright: resolvedBright,
    color: resolvedColor,
  };

  const byController = {};
  for (const it of crossMembers) {
    const cid = it.controllerId;
    if (!byController[cid]) byController[cid] = [];
    byController[cid].push(it);
  }

  for (const controllerId of Object.keys(byController)) {
    const group = byController[controllerId];
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];

    for (const it of group) {
      // OFF: 멤버 저장 bright/color 유지. ON: 호스트 look(물리 점등용)
      const memberBright = it.item?.bright != null ? parseInt(it.item.bright, 10) : 0;
      const memberColor = it.item?.color != null ? parseInt(it.item.color, 10) : 0;
      const useBright = resolvedOnoff === 0
        ? (Number.isFinite(memberBright) ? memberBright : 0)
        : (Number.isFinite(resolvedBright) ? resolvedBright : 0);
      const useColor = resolvedOnoff === 0
        ? (Number.isFinite(memberColor) ? memberColor : 0)
        : (Number.isFinite(resolvedColor) ? resolvedColor : 0);
      for (const p of lampPortsOf(it)) {
        portsArr.push(p);
        onoffArr.push(resolvedOnoff);
        brightArr.push(useBright);
        colorArr.push(useColor);
      }
    }
    if (portsArr.length === 0) continue;

    if (supportsLampCmdCs(controllerId)) {
      publishControlSilent(mqttControl, controllerId, {
        serial: controllerId,
        cmd: 'cs',
        type: DeviceType.Lamp,
        origin,
        data: {
          ports: portsArr,
          onoff: onoffArr,
          bright: brightArr,
          color: colorArr,
        },
      });
    } else {
      for (const it of group) {
        const ports = lampPortsOf(it);
        if (ports.length === 0) continue;
        const memberBright = it.item?.bright != null ? parseInt(it.item.bright, 10) : 0;
        const memberColor = it.item?.color != null ? parseInt(it.item.color, 10) : 0;
        const useBright = resolvedOnoff === 0
          ? (Number.isFinite(memberBright) ? memberBright : 0)
          : (Number.isFinite(resolvedBright) ? resolvedBright : 0);
        const useColor = resolvedOnoff === 0
          ? (Number.isFinite(memberColor) ? memberColor : 0)
          : (Number.isFinite(resolvedColor) ? resolvedColor : 0);
        publishControlSilent(mqttControl, controllerId, {
          serial: controllerId,
          cmd: 'c',
          type: DeviceType.Lamp,
          origin,
          data: {
            ports,
            onoff: resolvedOnoff,
            bright: useBright,
            color: useColor,
          },
        });
      }
    }
  }
}

/**
 * 물리 버튼(mappingType=1) 조작 시 다른 M24/보드 mapped 조명에 버튼 bright/color/onoff 적용.
 * cross-room 멤버 DB/UI 기본값은 건드리지 않음 (MQTT만, store 미갱신).
 */
export async function publishMappedButtonCrossControllerLamps({
  mqttControl,
  buttonDeviceId,
  sourceControllerId,
  onoff,
  bright,
  color,
}) {
  if (!mqttControl || buttonDeviceId == null || !sourceControllerId) return;

  const items = await fetchPhysicalMappingItems(buttonDeviceId);
  publishCrossControllerFromItems({
    mqttControl,
    buttonDeviceId,
    sourceControllerId,
    items,
    onoff,
    bright,
    color,
  });
}
